import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAppStore } from './store/appStore';
import { useAuthStore } from './store/authStore';

const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const FleetPage = React.lazy(() => import('./pages/FleetPage'));
const DriversPage = React.lazy(() => import('./pages/DriversPage'));
const RoutesPage = React.lazy(() => import('./pages/RoutesPage'));
const TicketingPage = React.lazy(() => import('./pages/TicketingPage'));
const DispatchPage = React.lazy(() => import('./pages/DispatchPage'));
const GpsPage = React.lazy(() => import('./pages/GpsPage'));
const FuelPage = React.lazy(() => import('./pages/FuelPage'));
const WorkshopPage = React.lazy(() => import('./pages/WorkshopPage'));
const WarehousePage = React.lazy(() => import('./pages/WarehousePage'));
const FinancePage = React.lazy(() => import('./pages/FinancePage'));
const CrmPage = React.lazy(() => import('./pages/CrmPage'));
const ParcelPage = React.lazy(() => import('./pages/ParcelPage'));
const LicensesPage = React.lazy(() => import('./pages/LicensesPage'));
const AetrPage = React.lazy(() => import('./pages/AetrPage'));
const InsurancePage = React.lazy(() => import('./pages/InsurancePage'));
const ArchivePage = React.lazy(() => import('./pages/ArchivePage'));
const DmsPage = React.lazy(() => import('./pages/DmsPage'));
const HrPage = React.lazy(() => import('./pages/HrPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { theme: appTheme, setTheme } = useAppStore();
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', appTheme === 'dark');
  }, [appTheme]);

  useEffect(() => {
    const savedLang = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(savedLang);
  }, [i18n]);

  return (
    <ConfigProvider
      theme={{
        algorithm: appTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
        },
      }}
    >
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner fullScreen tip="Loading..." />}>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/fleet/*" element={<FleetPage />} />
              <Route path="/drivers/*" element={<DriversPage />} />
              <Route path="/routes/*" element={<RoutesPage />} />
              <Route path="/ticketing/*" element={<TicketingPage />} />
              <Route path="/dispatch/*" element={<DispatchPage />} />
              <Route path="/gps/*" element={<GpsPage />} />
              <Route path="/fuel/*" element={<FuelPage />} />
              <Route path="/workshop/*" element={<WorkshopPage />} />
              <Route path="/warehouse/*" element={<WarehousePage />} />
              <Route path="/finance/*" element={<FinancePage />} />
              <Route path="/crm/*" element={<CrmPage />} />
              <Route path="/parcel/*" element={<ParcelPage />} />
              <Route path="/licenses/*" element={<LicensesPage />} />
              <Route path="/aetr/*" element={<AetrPage />} />
              <Route path="/insurance/*" element={<InsurancePage />} />
              <Route path="/archive/*" element={<ArchivePage />} />
              <Route path="/dms/*" element={<DmsPage />} />
              <Route path="/hr/*" element={<HrPage />} />
              <Route path="/reports/*" element={<ReportsPage />} />
              <Route path="/admin/*" element={<AdminPage />} />
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ConfigProvider>
  );
};

export default App;
